/**
 * `normalizeRpe`: decoded RPE JSON -> IR (W1-07a formats-json-rpe; DESIGN.md §7.2 normalisation table, contract
 * formats/rpe/json.ts `NormalizeRpeFn`).
 *
 * Exact for well-defined input; everything else is repaired and reported:
 *  - decoder diagnostics: what Phira rejects (float in an integer field, missing required field, wrong type,
 *    negative unsigned, BOM / leading whitespace / invalid UTF-8 / no "META", attachUI not a name, non-numeric
 *    control keys) and where it panics (control point without its value key) -> 'changed', phiraWouldLoad false;
 *    unexpected values of RPE-only fields and of the META strings only Phira's metadata inference reads (it skips
 *    them silently, F§2.2) -> 'normalized', the chart still loads;
 *  - BPM list: invalid points dropped, empty list replaced, unsorted list sorted, first point moved to beat 0 by
 *    shifting every beat (normTempo.ts);
 *  - event lists: invalid beats refused, unsorted / overlapping / zero-length events rebuilt as Phira plays them
 *    forward, speed in Phira's 1.7 integrator compressed (normEvents.ts; speed mode from the extended context);
 *  - `[]` lists: one participating `[]` = absent (exact); `[]` in the first participating layer of several makes
 *    Phira fall back (alpha 1, others 0): alpha becomes a constant 255 track, other properties absent, the ignored
 *    later layers move to the sidecar ('changed'); `[]` after a non-empty layer crashes Phira: dropped ('changed');
 *    empty speed / scale / colour / incline lists are skipped by Phira: absent (exact); `textEvents: []` /
 *    `paintEvents: []` on line.png: a text "" / paint -1 constant at beat 0 (exact unless the x0.5 text rule
 *    changes the line's scaleX), likewise a non-empty one whose events all collapse (Phira showed its default);
 *    `gifEvents: []` on a GIF texture: kept (auto-play);
 *  - lists Phira ignores for drawing are kept in `ignoredLists` (text / paint on a textured line, gif on line.png)
 *    as written, except that Phira still parses them: events with an invalid beat are removed ('changed'), integer
 *    fields rounded and floats made finite (the decoder reported those repairs); gifEvents on a texture that is
 *    not a GIF (Phira's GIF decoder fails the whole chart) move to the sidecar: 'changed', phiraWouldLoad false. A
 *    texture the package lacks (textureKind null) is classified by its file extension: the missing file itself is
 *    the import's fatal PH-01, and the GIF data stays typed for when the file is added;
 *  - explicit `null` for a known list -> `nullKeys` (exact);
 *  - bezier on speed events -> bezier 0 with the easingType kept, original fields in the sidecar: 'changed' when
 *    Phira's 1.7 integrator evaluates the curve (easingType >= 2; phiraWouldLoad false unless a non-speed event
 *    registers the same Bezier key), 'normalized' when it does not, and in legacy mode a flagged kept row;
 *  - notes: invalid beats refused, unknown types become taps, reversed holds end at their start; raw above /
 *    isFake / alpha values kept (exact);
 *  - father: out of range, below -1 or part of a cycle -> parent cleared ('changed', Phira panics or refuses);
 *  - numbers: beyond the f64 range (`1e400`) where serde_json reads them -> replaced ('changed', Phira refuses),
 *    in data Phira skips (passthrough, RPE-only fields, skewControl) the decoder's stand-in is kept ('normalized');
 *    f32 values beyond the f32 range (`1e39`, an infinity in Phira) in events, notes and control points -> the
 *    largest f32 ('normalized', one entry per list); out-of-range whole numbers -> clamped ('changed');
 *  - text: unpaired surrogate escapes, which the decoder replaced by U+FFFD, are reported from its diagnostics
 *    ('changed' where Phira reads the text or key, 'normalized' where it skips it);
 *  - beats the IR cannot hold once every beat is shifted by the first BPM point's beat (beyond i32, or a fraction
 *    finer than a triple holds; normRange.ts) -> the event, note, BPM point or time tag is removed ('changed';
 *    time tags 'normalized'); beats normalisation adds (a final jump's tail, the default pad, baked nodes) always
 *    stay inside the range. Lists Phira does not draw move with the shift like every other list.
 * Normalisation never throws on a decoded chart, and every IR it returns encodes.
 *
 * The contracted report holds only what normalisation CHANGED ('normalized' and 'changed'), so the export
 * self-check can require it to be empty for our own output. Rows the table keeps but flags ('flagged', 'rpeOnly':
 * lead-ins, animated incline, inert controls, ignored lists, ...) come from `rpeKeptReport` (kept.ts).
 * Data moved to phiedit.json is returned by `normalizeRpeDetailed` (`sidecar`) and by `sidecarEntriesOf`.
 */

import type {
  IrBpm,
  IrChart,
  IrControlPoint,
  IrEvent,
  IrEventList,
  IrExtended,
  IrLayer,
  IrLine,
  IrMeta,
  IrNote,
  IrTimeTag,
  NormalizeResult,
} from '../../core/ir/types';
import type { SpeedMode } from '../../core/model/types';
import { bezierKey, roundHalfAwayFromZero } from '../../core/ease/bezierKey';
import type { BezierPoints } from '../../core/ease/types';
import { Beats } from '../../core/time/beat';
import type { Triple } from '../../core/time/types';
import { setOwn } from '../../core/util/json';
import type { Json, MessageRef, NonEmpty, RGB } from '../../core/util/types';
import type {
  DecodeDiagnostic,
  NormalizeContext,
  NormalizeRpeFn,
  RpeChart,
  RpeControlPoint,
  RpeDecoded,
  RpeEvent,
  RpeEventLayer,
  RpeExtended,
  RpeJudgeLine,
  RpeNote,
} from './json';
import { clampF32, toInt } from './normCoerce';
import { F32Clamps, normalizeEventList } from './normEvents';
import type { ListContext, ListMode, ListTime, ListValue, ValueKind } from './normEvents';
import { fitsShifted } from './normRange';
import { NormReport } from './normReport';
import type { RpeSidecarEntry } from './normReport';
import { normalizeBpm } from './normTempo';
import type { TempoNorm } from './normTempo';
import type { DiagnosticReason } from './read';
import {
  CONTROL_CURVE_KEYS,
  CONTROL_CURVES,
  EXTENDED_LIST_KEYS,
  eventValueKind,
  fieldSpec,
  LAYER_LIST_KEYS,
} from './schema';
import type { ExtendedListKey, LayerListKey } from './schema';

export type { RpeSidecarEntry } from './normReport';

/** Speed integrator Phira will use (info.yml useRpe170Speed; NormalizeContext.speedMode). */
export type RpeSpeedMode = SpeedMode;

const I32_MIN = -2147483648;
const I32_MAX = 2147483647;

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

// ---------------------------------------------------------------------------------------------------------------
// Shared environment
// ---------------------------------------------------------------------------------------------------------------

/** What line-level normalisation needs; also built for fragments. */
export interface NormEnv {
  readonly report: NormReport;
  readonly time: ListTime;
  readonly speedMode: RpeSpeedMode;
  readonly textureKind: (path: string) => 'gif' | 'image' | null;
  /** True for whole charts (sidecar data is recorded); false for fragments. */
  readonly sidecar: boolean;
  /** Bezier keys registered by the chart's non-speed events (see `registeredBezierKeys`); absent for fragments. */
  readonly bezierRegistered?: (points: BezierPoints) => boolean;
}

/** Default texture classification without package facts: `.gif` paths are GIFs, everything else an image. */
export function defaultTextureKind(path: string): 'gif' | 'image' {
  return /\.gif$/i.test(path) ? 'gif' : 'image';
}

/** List time for a tempo: source seconds and the beat shift. */
export function listTime(tempo: Pick<TempoNorm, 'sec' | 'out' | 'zeroBeat' | 'shift'>): ListTime {
  return { sec: (t) => tempo.sec(t), out: (t, b) => tempo.out(t, b), zeroBeat: tempo.zeroBeat, shift: tempo.shift };
}

// ---------------------------------------------------------------------------------------------------------------
// JSON helpers (sidecar and ignored lists keep the source data)
// ---------------------------------------------------------------------------------------------------------------

function finiteOrNull(v: number): Json {
  return Number.isFinite(v) ? v : null;
}

export function eventToJson(e: RpeEvent<ListValue>): Json {
  const o: Record<string, Json> = {};
  const value = (v: ListValue): Json =>
    typeof v === 'number' ? finiteOrNull(v) : typeof v === 'string' ? v : [...v].map(finiteOrNull);
  if (e.bezier !== undefined) setOwn(o, 'bezier', e.bezier);
  if (e.bezierPoints !== undefined) setOwn(o, 'bezierPoints', e.bezierPoints.map(finiteOrNull));
  if (e.easingLeft !== undefined) setOwn(o, 'easingLeft', e.easingLeft);
  if (e.easingRight !== undefined) setOwn(o, 'easingRight', e.easingRight);
  if (e.easingType !== undefined) setOwn(o, 'easingType', e.easingType);
  setOwn(o, 'end', value(e.end));
  setOwn(o, 'endTime', e.endTime.map(finiteOrNull));
  if (e.linkgroup !== undefined) setOwn(o, 'linkgroup', e.linkgroup);
  setOwn(o, 'start', value(e.start));
  setOwn(o, 'startTime', e.startTime.map(finiteOrNull));
  for (const [k, v] of e.extra) if (!Object.prototype.hasOwnProperty.call(o, k)) setOwn(o, k, v);
  return o;
}

export function eventListToJson(list: readonly RpeEvent<ListValue>[]): Json {
  return list.map((e) => eventToJson(e));
}

// ---------------------------------------------------------------------------------------------------------------
// Diagnostics -> report
// ---------------------------------------------------------------------------------------------------------------

function fieldOf(path: string): string {
  const parts = path.replace(/\[\d+\]/g, '').split('.');
  return parts[parts.length - 1] ?? path;
}

function wrongTypeMessage(reason: DiagnosticReason | undefined, field: string): MessageRef {
  switch (reason) {
    case 'duplicateKey':
      return { key: 'rpe.report.duplicateKey', params: { field } };
    case 'outOfRange':
      return { key: 'rpe.report.outOfRange', params: { field } };
    case 'numberOverflow':
      return { key: 'rpe.report.numberOverflow', params: { field } };
    case 'unpairedSurrogate':
      return { key: 'rpe.report.unpairedSurrogate', params: { field } };
    case 'notKept':
      return { key: 'rpe.report.notKept', params: { field } };
    case 'otherFragment':
    case undefined:
      return { key: 'rpe.report.wrongType', params: { field } };
  }
}

function diagnosticMessage(d: DecodeDiagnostic): MessageRef {
  const field = fieldOf(d.path);
  switch (d.code) {
    case 'floatInIntField':
      return { key: 'rpe.report.floatInIntField', params: { field } };
    case 'missingRequired':
      return { key: 'rpe.report.missingRequired', params: { field } };
    case 'wrongType':
      return wrongTypeMessage(d.reason, field);
    case 'negativeUnsigned':
      return { key: 'rpe.report.negativeUnsigned', params: { field } };
    case 'tripleZeroDen':
      return { key: 'rpe.report.tripleZeroDen', params: { field } };
    case 'bom':
      return { key: 'rpe.report.bom' };
    case 'leadingWhitespace':
      return { key: 'rpe.report.leadingWhitespace' };
    case 'invalidUtf8':
      return { key: 'rpe.report.invalidUtf8' };
    case 'noMetaSubstring':
      return { key: 'rpe.report.noMetaSubstring' };
    case 'unknownAttachUI':
      return { key: 'rpe.report.unknownAttachUI', params: { value: d.detail ?? '' } };
    case 'controlMissingValueKey':
      return { key: 'rpe.report.controlMissingValueKey', params: { field: d.detail ?? field } };
    case 'controlNonNumericExtra':
      return { key: 'rpe.report.controlNonNumericExtra', params: { field } };
  }
}

/**
 * Turns decoder diagnostics into report entries: 'note' -> 'normalized' (Phira loads the chart), everything else
 * -> 'changed' and Phira would not load the source. Not reported here: the beat-triple c = 0 note (reported where
 * normalisation refuses the item) and a fragment's members that belong to another fragment (ignored, not repaired).
 */
export function reportDiagnostics(diagnostics: readonly DecodeDiagnostic[], report: NormReport): void {
  for (const d of diagnostics) {
    if (d.code === 'tripleZeroDen') continue;
    const reason = d.reason;
    if (reason === 'otherFragment') continue;
    if (d.severity === 'note') {
      const meta = /^META\.([A-Za-z]+)$/.exec(d.path)?.[1];
      const info = meta !== undefined && fieldSpec('meta', meta)?.readsOutsideParser === true;
      const params = { field: fieldOf(d.path) };
      let message: MessageRef = { key: 'rpe.report.rpeOnlyValue', params };
      if (reason === 'numberOverflow') message = { key: 'rpe.report.numberOverflowIgnored', params };
      else if (reason === 'unpairedSurrogate') message = { key: 'rpe.report.unpairedSurrogateIgnored', params };
      else if (reason === 'notKept') message = { key: 'rpe.report.notKept', params };
      else if (info) message = { key: 'rpe.report.metaInfoValue', params };
      report.add('normalized', message, d.path);
      continue;
    }
    report.phiraWouldNotLoad();
    report.add('changed', diagnosticMessage(d), d.path === '' ? undefined : d.path);
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Event lists of a line
// ---------------------------------------------------------------------------------------------------------------

function listCtx(
  env: NormEnv,
  key: LayerListKey | ExtendedListKey,
  path: string,
  line: number | undefined,
  sidecarKey: string,
  def?: ListValue,
): ListContext {
  let mode: ListMode = 'standard';
  if (key === 'speedEvents') mode = env.speedMode === 'rpe170' ? 'speed170' : 'speedLegacy';
  else if (key === 'gifEvents') mode = 'gif';
  else if (key === 'inclineEvents') mode = 'incline';
  const base: ListContext = {
    key,
    kind: eventValueKind(key),
    mode,
    path,
    line,
    time: env.time,
    report: env.report,
    sidecarKey: env.sidecar ? sidecarKey : null,
    ...(env.bezierRegistered === undefined ? {} : { bezierRegistered: env.bezierRegistered }),
  };
  return def === undefined ? base : { ...base, def };
}

function asList<V>(events: readonly IrEvent<ListValue>[]): IrEventList<V> | null {
  return events.length === 0 ? null : (events as unknown as IrEventList<V>);
}

/** A constant event [beat 0, beat 1] in OUTPUT beats (time 0 after the beat shift). */
function constantAtZero<V extends ListValue>(v: V): IrEventList<V> {
  return [
    {
      startTime: [0, 0, 1],
      endTime: [1, 0, 1],
      start: v,
      end: v,
      easingType: 1,
      easingLeft: 0,
      easingRight: 1,
      bezier: 0,
      bezierPoints: [0, 0, 0, 0],
      linkgroup: 0,
      extra: [],
      origin: 'segment',
      srcKey: null,
    },
  ];
}

type Fate = 'keep' | 'drop' | 'sidecar' | 'alpha255';

/** Phira's empty-list rules across layers (F§6.2) for the summed properties. */
function layerFates(
  slots: readonly (RpeEventLayer | null)[],
  path: string,
  li: number | undefined,
  env: NormEnv,
): Map<string, Fate> {
  const fates = new Map<string, Fate>();
  for (const key of LAYER_LIST_KEYS) {
    const participating: number[] = [];
    slots.forEach((slot, i) => {
      const list = slot?.[key];
      if (list !== undefined && list !== null) participating.push(i);
    });
    if (participating.length === 0) continue;
    const listAt = (i: number): readonly unknown[] => slots[i]?.[key] ?? [];
    if (key === 'speedEvents') {
      // Empty speed lists are skipped by both integrators (F§6.2): absent, exactly.
      for (const i of participating) if (listAt(i).length === 0) fates.set(`${String(i)}:${key}`, 'drop');
      continue;
    }
    const first = participating[0] ?? 0;
    if (participating.length === 1) {
      if (listAt(first).length === 0) fates.set(`${String(first)}:${key}`, 'drop');
      continue;
    }
    if (listAt(first).length === 0) {
      fates.set(`${String(first)}:${key}`, key === 'alphaEvents' ? 'alpha255' : 'drop');
      for (const i of participating.slice(1))
        fates.set(`${String(i)}:${key}`, listAt(i).length === 0 ? 'drop' : 'sidecar');
      env.report.add(
        'changed',
        key === 'alphaEvents' ? { key: 'rpe.report.emptyFirstLayerAlpha' } : { key: 'rpe.report.emptyFirstLayer' },
        `${path}.eventLayers[${String(first)}].${key}`,
        li,
      );
      continue;
    }
    for (const i of participating.slice(1)) {
      if (listAt(i).length !== 0) continue;
      fates.set(`${String(i)}:${key}`, 'drop');
      env.report.phiraWouldNotLoad();
      env.report.add('changed', { key: 'rpe.report.emptyLaterLayer' }, `${path}.eventLayers[${String(i)}].${key}`, li);
    }
  }
  return fates;
}

function normalizeLayers(src: RpeJudgeLine, path: string, li: number | undefined, env: NormEnv): (IrLayer | null)[] {
  const fates = layerFates(src.eventLayers, path, li, env);
  return src.eventLayers.map((slot, i) => {
    if (slot === null) return null;
    const layer: Mutable<IrLayer> = { nullKeys: [], extra: slot.extra };
    const nullKeys: string[] = [];
    for (const key of LAYER_LIST_KEYS) {
      const list = slot[key];
      if (list === undefined) continue;
      if (list === null) {
        nullKeys.push(key);
        continue;
      }
      const fate = fates.get(`${String(i)}:${key}`) ?? 'keep';
      const lpath = `${path}.eventLayers[${String(i)}].${key}`;
      if (fate === 'drop') continue;
      if (fate === 'sidecar') {
        if (env.sidecar && li !== undefined)
          env.report.toSidecar(li, `eventLayers[${String(i)}].${key}`, eventListToJson(list));
        continue;
      }
      if (fate === 'alpha255') {
        layer.alphaEvents = constantAtZero(255);
        continue;
      }
      const ir = asList<number>(
        normalizeEventList(list, listCtx(env, key, lpath, li, `eventLayers[${String(i)}].${key}`)),
      );
      if (ir !== null) layer[key] = ir;
    }
    layer.nullKeys = nullKeys;
    return layer;
  });
}

function ignoredValue(v: ListValue, kind: ValueKind, clamps: F32Clamps, path: string, i: number, field: string): Json {
  if (kind === 'string') return typeof v === 'string' ? v : '';
  if (kind === 'rgb') return typeof v === 'object' ? [...rgbOf(v)] : [255, 255, 255];
  return typeof v === 'number' ? clamps.f32(v, path, i, field) : 0;
}

/**
 * The JSON of a list Phira does not draw (`ignoredLists`, written back as kept). Phira still parses it with its
 * event struct, so it keeps Phira's typing: events with an invalid beat (c = 0, negative or fractional parts,
 * malformed) are removed and reported, integer fields are rounded into their range and float fields made finite
 * (the decoder already reported those values; numbers beyond the f32 range become the largest f32, reported as
 * 'normalized'). Its beats move with the chart's beat shift like every other beat (their times count for Phira's
 * max_time); an event whose shifted beat a triple cannot hold is removed ('changed'). Everything else, unknown
 * keys included, stays as written (triples too when there is no shift).
 */
function ignoredListToJson(
  list: readonly RpeEvent<ListValue>[],
  kind: ValueKind,
  path: string,
  li: number | undefined,
  env: NormEnv,
): Json {
  const out: Json[] = [];
  let refused = 0;
  let refusedPath: string | undefined;
  let outOfRange = 0;
  let outOfRangePath: string | undefined;
  const clamps = new F32Clamps();
  list.forEach((e, i) => {
    const s = Beats.tryFromTriple(e.startTime);
    const t = Beats.tryFromTriple(e.endTime);
    if (s === null || t === null) {
      refused++;
      refusedPath ??= `${path}[${String(i)}]`;
      return;
    }
    if (!fitsShifted(s.beat, env.time.shift) || !fitsShifted(t.beat, env.time.shift)) {
      outOfRange++;
      outOfRangePath ??= `${path}[${String(i)}]`;
      return;
    }
    const o: Record<string, Json> = {};
    if (e.bezier !== undefined) setOwn(o, 'bezier', toInt(e.bezier, 0, 255, 0));
    if (e.bezierPoints !== undefined) {
      setOwn(
        o,
        'bezierPoints',
        e.bezierPoints.map((v) => clamps.f32(v, path, i, 'bezierPoints')),
      );
    }
    if (e.easingLeft !== undefined) setOwn(o, 'easingLeft', clamps.f32(e.easingLeft, path, i, 'easingLeft'));
    if (e.easingRight !== undefined) setOwn(o, 'easingRight', clamps.f32(e.easingRight, path, i, 'easingRight'));
    if (e.easingType !== undefined) setOwn(o, 'easingType', toInt(e.easingType, I32_MIN, I32_MAX, 1));
    setOwn(o, 'end', ignoredValue(e.end, kind, clamps, path, i, 'end'));
    setOwn(o, 'endTime', [...env.time.out(e.endTime, t.beat)]);
    if (e.linkgroup !== undefined) setOwn(o, 'linkgroup', toInt(e.linkgroup, I32_MIN, I32_MAX, 0));
    setOwn(o, 'start', ignoredValue(e.start, kind, clamps, path, i, 'start'));
    setOwn(o, 'startTime', [...env.time.out(e.startTime, s.beat)]);
    for (const [k, v] of e.extra) if (!Object.prototype.hasOwnProperty.call(o, k)) setOwn(o, k, v);
    out.push(o);
  });
  if (refused > 0) env.report.add('changed', { key: 'rpe.report.eventInvalidTime' }, refusedPath, li, refused);
  if (outOfRange > 0) {
    env.report.add('changed', { key: 'rpe.report.eventBeatOutOfRange' }, outOfRangePath, li, outOfRange);
  }
  clamps.report(env.report, li);
  return out;
}

function hasEvents(list: readonly unknown[] | null | undefined): boolean {
  return list !== undefined && list !== null && list.length > 0;
}

function normalizeExtended(
  src: RpeJudgeLine,
  ext: RpeExtended,
  textureKind: IrLine['textureKind'],
  path: string,
  li: number | undefined,
  env: NormEnv,
): IrExtended {
  const out: Mutable<IrExtended> = { ignoredLists: [], nullKeys: [], extra: ext.extra };
  const ignored: Array<readonly [string, Json]> = [];
  const nullKeys: string[] = [];
  const builtin = textureKind === 'builtin';
  for (const key of EXTENDED_LIST_KEYS) {
    const list = ext[key] as readonly RpeEvent<ListValue>[] | null | undefined;
    if (list === undefined) continue;
    if (list === null) {
      nullKeys.push(key);
      continue;
    }
    const lpath = `${path}.extended.${key}`;
    const sideKey = `extended.${key}`;
    const run = (def?: ListValue): IrEvent<ListValue>[] =>
      normalizeEventList(list, listCtx(env, key, lpath, li, sideKey, def));
    switch (key) {
      case 'textEvents':
      case 'paintEvents': {
        if (!builtin) {
          // Phira ignores text / paint on a textured line (kept for RPE, still scanned like Phira does).
          ignored.push([key, ignoredListToJson(list, eventValueKind(key), lpath, li, env)]);
          break;
        }
        if (list.length === 0) {
          // An empty list still makes a text / paint line (F§9.5): "" / -1 from beat 0 is the same line ...
          if (key === 'textEvents') {
            out.textEvents = constantAtZero('');
            // ... except that a non-empty textEvents switches off the x0.5 scaleX rule (F§9.1).
            if (hasEvents(ext.scaleXEvents) && (src.attachUI ?? null) === null) {
              env.report.add('changed', { key: 'rpe.report.emptyTextScale' }, lpath, li);
            }
          } else {
            out.paintEvents = constantAtZero(-1);
          }
          break;
        }
        const ir = run(key === 'textEvents' ? '' : -1);
        // Nothing left (e.g. a single zero-length event): Phira showed the default from t = 0 on, and the list's
        // presence still makes this a text / paint line, so it becomes that default from beat 0.
        if (key === 'textEvents') out.textEvents = asList<string>(ir) ?? constantAtZero('');
        else out.paintEvents = asList<number>(ir) ?? constantAtZero(-1);
        break;
      }
      case 'gifEvents': {
        if (builtin) {
          ignored.push([key, ignoredListToJson(list, 'f32', lpath, li, env)]);
          break;
        }
        if (textureKind !== 'gif') {
          // Phira decodes the texture as a GIF as soon as gifEvents exists: a PNG/JPG fails the whole chart.
          if (env.sidecar && li !== undefined) env.report.toSidecar(li, sideKey, eventListToJson(list));
          env.report.phiraWouldNotLoad();
          env.report.add('changed', { key: 'rpe.report.gifOnNonGif', params: { texture: src.Texture } }, lpath, li);
          break;
        }
        if (list.length === 0) {
          out.gifEvents = [];
          break;
        }
        const l = asList<number>(run());
        if (l !== null) out.gifEvents = l;
        break;
      }
      case 'colorEvents': {
        if (list.length === 0) break;
        const l = asList<RGB>(run([255, 255, 255]));
        if (l !== null) out.colorEvents = l;
        break;
      }
      case 'inclineEvents': {
        if (list.length === 0) break;
        const l = asList<number>(run(0));
        if (l !== null) out.inclineEvents = l;
        break;
      }
      case 'scaleXEvents':
      case 'scaleYEvents': {
        if (list.length === 0) break;
        const l = asList<number>(run());
        if (l !== null) out[key] = l;
        break;
      }
    }
  }
  out.ignoredLists = ignored;
  out.nullKeys = nullKeys;
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Notes, controls, line fields
// ---------------------------------------------------------------------------------------------------------------

function rgbOf(c: readonly [number, number, number]): RGB {
  return [toInt(c[0], 0, 255, 255), toInt(c[1], 0, 255, 255), toInt(c[2], 0, 255, 255)];
}

function finiteOr(v: number | undefined, def: number): number {
  return v !== undefined && Number.isFinite(v) ? v : def;
}

/** Notes of a line (or a notes fragment). */
export function normalizeNotes(
  notes: readonly RpeNote[],
  path: string,
  li: number | undefined,
  env: NormEnv,
): IrNote[] {
  const out: IrNote[] = [];
  let refused = 0;
  let refusedPath: string | undefined;
  let outOfRange = 0;
  let outOfRangePath: string | undefined;
  const clamps = new F32Clamps();
  notes.forEach((n, ni) => {
    const npath = `${path}[${String(ni)}]`;
    const s = Beats.tryFromTriple(n.startTime);
    const e = Beats.tryFromTriple(n.endTime);
    if (s === null || e === null) {
      refused++;
      refusedPath ??= npath;
      return;
    }
    if (!fitsShifted(s.beat, env.time.shift) || !fitsShifted(e.beat, env.time.shift)) {
      // Once the chart starts at beat 0 at its first BPM point, a triple cannot hold this note's beats.
      outOfRange++;
      outOfRangePath ??= npath;
      return;
    }
    const f32 = (v: number, field: string): number => clamps.f32(v, path, ni, field);
    let type = toInt(n.type, 0, 255, 1);
    if (type < 1 || type > 4) {
      env.report.phiraWouldNotLoad();
      env.report.add('changed', { key: 'rpe.report.unknownNoteType', params: { type } }, `${npath}.type`, li);
      type = 1;
    }
    let endTime = env.time.out(n.endTime, e.beat);
    if (type === 2 && Beats.lt(e.beat, s.beat)) {
      env.report.add('changed', { key: 'rpe.report.holdReversed' }, npath, li);
      endTime = env.time.out(n.startTime, s.beat);
    }
    const note: Mutable<IrNote> = {
      type: type as IrNote['type'],
      startTime: env.time.out(n.startTime, s.beat),
      endTime,
      positionX: f32(finiteOr(n.positionX, 0), 'positionX'),
      above: toInt(n.above, 0, 255, 1),
      isFake: toInt(n.isFake, 0, 255, 0),
      speed: f32(finiteOr(n.speed, 1), 'speed'),
      yOffset: f32(finiteOr(n.yOffset, 0), 'yOffset'),
      size: f32(finiteOr(n.size, 1), 'size'),
      alpha: toInt(n.alpha, 0, 65535, 255),
      visibleTime: finiteOr(n.visibleTime, 999999),
      extra: n.extra,
      srcNote: null,
    };
    if (typeof n.hitsound === 'string') note.hitsound = n.hitsound;
    if (typeof n.judgeArea === 'number') note.judgeArea = f32(n.judgeArea, 'judgeArea');
    if (n.tint !== undefined && n.tint !== null) note.tint = rgbOf(n.tint);
    if (n.tintHitEffects !== undefined && n.tintHitEffects !== null) note.tintHitEffects = rgbOf(n.tintHitEffects);
    out.push(note);
  });
  if (refused > 0) env.report.add('changed', { key: 'rpe.report.noteInvalidTime' }, refusedPath, li, refused);
  if (outOfRange > 0) {
    env.report.add('changed', { key: 'rpe.report.noteBeatOutOfRange' }, outOfRangePath, li, outOfRange);
  }
  clamps.report(env.report, li);
  return out;
}

export interface ControlsIrOptions {
  /** The curve's value key ('pos', 'alpha', 'size', 'y' or 'skew'; report paths). Default 'value'. */
  readonly valueKey?: string;
  /** Value of a point without its value key (Phira panicked): 1 for Phira's curves (default), 0 for skew. */
  readonly def?: number;
  /** Where clamped numbers are reported ('normalized'); not reported without one. */
  readonly report?: NormReport;
  /** JSON path of the curve ('' for a fragment). */
  readonly path?: string;
  readonly line?: number;
}

/**
 * Control points: `def` where the value key was missing, only numeric extra keys. Phira reads the value and every
 * extra key as f32 (F§12): a number beyond the f32 range (`1e39`) is an infinity there, which the writer cannot
 * write; it becomes the largest f32 of the same sign ('normalized': Phira never reads the extra keys, and an
 * infinite value only differs from it where Phira multiplies it by 0). Numbers beyond the f64 range were reported
 * by the decoder.
 */
export function controlsToIr(points: readonly RpeControlPoint[], opts: ControlsIrOptions = {}): IrControlPoint[] {
  const base = opts.path ?? '';
  let clamped = 0;
  let clampedPath: string | undefined;
  let clampedKey = '';
  const f32 = (v: number, i: number, key: string): number => {
    if (Number.isFinite(v) && !Number.isFinite(Math.fround(v))) {
      clamped++;
      if (clampedPath === undefined) {
        clampedPath = `${base}[${String(i)}].${key}`;
        clampedKey = key;
      }
    }
    return clampF32(v);
  };
  const out = points.map((p, i) => ({
    x: finiteOr(p.x, 0),
    easing: toInt(p.easing, 0, 255, 1),
    value: f32(finiteOr(p.value, opts.def ?? 1), i, opts.valueKey ?? 'value'),
    extra: p.extra.flatMap(([k, v]) => (typeof v === 'number' ? [[k, f32(v, i, k)] as const] : [])),
  }));
  if (clamped > 0) {
    opts.report?.add(
      'normalized',
      { key: 'rpe.report.controlValueClamped', params: { field: clampedKey } },
      clampedPath,
      opts.line,
      clamped,
    );
  }
  return out;
}

function textureKindOf(texture: string, env: NormEnv): IrLine['textureKind'] {
  if (texture === 'line.png') return 'builtin';
  // A file the package lacks (null) is the import's fatal PH-01; its extension decides until the file is added,
  // so GIF progress on a missing `.gif` stays typed instead of moving to the sidecar.
  const kind = env.textureKind(texture) ?? defaultTextureKind(texture);
  return kind === 'gif' ? 'gif' : 'image';
}

/**
 * Raw father value, rounded as the other integer fields (validated later: against the line count for a chart,
 * with `fatherNeverValid` for a 'line' fragment).
 */
function rawFather(src: Pick<RpeJudgeLine, 'father'>): number {
  const f = src.father;
  if (f === undefined || f === null || !Number.isFinite(f)) return -1;
  const r = Number.isInteger(f) ? f : roundHalfAwayFromZero(f);
  return r === 0 ? 0 : r;
}

/** True when no chart can have this father: below -1 or outside i32 (Phira refuses or panics). */
export function fatherNeverValid(father: number): boolean {
  return father !== -1 && (father < -1 || father > I32_MAX);
}

/** The line's own fields (no event lists / notes), shared with the 'line' fragment (`path` '' there). */
export function lineFieldsToIr(
  src: Omit<RpeJudgeLine, 'eventLayers' | 'extended' | 'notes'> & { readonly notes?: RpeJudgeLine['notes'] },
  env: NormEnv,
  path = '',
  li?: number,
): IrLine {
  const textureKind = textureKindOf(src.Texture, env);
  const anchor = src.anchor;
  const line: Mutable<IrLine> = {
    srcLine: null,
    Name: src.Name,
    Texture: src.Texture,
    textureKind,
    father: rawFather(src),
    rotateWithFather: src.rotateWithFather ?? false,
    isCover: toInt(src.isCover, 0, 255, 1),
    zOrder: toInt(src.zOrder, I32_MIN, I32_MAX, 0),
    attachUI: src.attachUI ?? null,
    Group: toInt(src.Group, I32_MIN, I32_MAX, 0),
    bpmfactor: finiteOr(src.bpmfactor, 1),
    anchor: anchor !== undefined && anchor.every((v) => Number.isFinite(v)) ? [anchor[0], anchor[1]] : [0.5, 0.5],
    isGif: src.isGif ?? textureKind === 'gif',
    numOfNotes: toInt(src.numOfNotes ?? (src.notes ?? []).filter((n) => n.isFake === 0).length, I32_MIN, I32_MAX, 0),
    eventLayers: [],
    extended: 'absent',
    notes: [],
    notesKey: 'absent',
    extra: src.extra,
  };
  for (const key of CONTROL_CURVE_KEYS) {
    const pts = src[key];
    if (pts === undefined) continue;
    line[key] = controlsToIr(pts, {
      valueKey: CONTROL_CURVES[key],
      report: env.report,
      path: path === '' ? key : `${path}.${key}`,
      ...(li === undefined ? {} : { line: li }),
    });
  }
  if (src.skewControl !== undefined) line.skewControl = src.skewControl;
  return line;
}

function normalizeLine(src: RpeJudgeLine, li: number, env: NormEnv): IrLine {
  const path = `judgeLineList[${String(li)}]`;
  const line: Mutable<IrLine> = lineFieldsToIr(src, env, path, li);
  line.eventLayers = normalizeLayers(src, path, li, env);
  if (src.extended === null) line.extended = null;
  else if (src.extended !== undefined) {
    line.extended = normalizeExtended(src, src.extended, line.textureKind, path, li, env);
  }
  if (src.notes !== undefined && src.notes !== null) {
    line.notes = normalizeNotes(src.notes, `${path}.notes`, li, env);
    line.notesKey = 'present';
  }
  return line;
}

/** father: -1 for none; out of range / below -1 / cycles cleared (Phira panics or refuses the chart). */
function fixFathers(lines: Mutable<IrLine>[], report: NormReport): void {
  lines.forEach((line, i) => {
    const f = line.father;
    if (f === -1) return;
    if (f < -1 || f >= lines.length || f === i) {
      report.phiraWouldNotLoad();
      report.add(
        'changed',
        f === i ? { key: 'rpe.report.fatherCycle' } : { key: 'rpe.report.fatherInvalid', params: { father: f } },
        `judgeLineList[${String(i)}].father`,
        i,
      );
      line.father = -1;
    }
  });
  // Cycles: walk each line's parent chain once. 1 = on the walk in progress, 2 = known to reach a root line, so
  // the whole check is linear. A chain that comes back to a line of the same walk is cut at the line that closes
  // the loop (the walks start from the lowest index, so the cut line is the same as in a walk per line).
  const state = new Uint8Array(lines.length);
  for (let i = 0; i < lines.length; i++) {
    if (state[i] === 2) continue;
    const walk: number[] = [];
    let cur = i;
    for (;;) {
      walk.push(cur);
      state[cur] = 1;
      const node = lines[cur];
      if (node === undefined || node.father === -1) break;
      const next = node.father;
      if (state[next] === 2) break;
      if (state[next] === 1) {
        report.phiraWouldNotLoad();
        report.add('changed', { key: 'rpe.report.fatherCycle' }, `judgeLineList[${String(cur)}].father`, cur);
        node.father = -1;
        break;
      }
      cur = next;
    }
    for (const j of walk) state[j] = 2;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// META and root
// ---------------------------------------------------------------------------------------------------------------

function metaToIr(decoded: RpeDecoded, report: NormReport): IrMeta {
  const m = decoded.chart.META;
  let version: IrMeta['RPEVersion'] = decoded.rpeVersionEffective;
  if (decoded.rpeVersionRaw !== null) version = decoded.rpeVersionRaw;
  else {
    const v = m.RPEVersion;
    const isPlainInt = typeof v === 'number' && Number.isInteger(v) && v >= I32_MIN && v <= I32_MAX;
    // A decoder finding there (a number beyond the f64 range, a duplicate key) is already reported as 'changed'.
    const diagnosed = decoded.diagnostics.some((d) => d.path.startsWith('META.RPEVersion'));
    if (v !== undefined && !isPlainInt && !diagnosed) {
      // Phira reads this as 160 (F§2.2); the same effective value is written as an integer.
      report.add('normalized', { key: 'rpe.report.rpeVersionReplaced' }, 'META.RPEVersion');
    }
  }
  return {
    RPEVersion: version,
    rpeVersionEffective: decoded.rpeVersionEffective,
    offset: toInt(m.offset, I32_MIN, I32_MAX, 0),
    name: m.name ?? '',
    level: m.level ?? '',
    charter: m.charter ?? '',
    composer: m.composer ?? '',
    illustration: m.illustration ?? '',
    background: m.background ?? '',
    song: m.song ?? '',
    id: m.id ?? '',
    extra: m.extra,
  };
}

/** Every beat triple of the chart (for the unsorted-BPM identity check). */
function* chartTriples(chart: RpeChart): Generator<Triple> {
  for (const line of chart.judgeLineList) {
    const lists: Array<readonly RpeEvent<unknown>[] | null | undefined> = [];
    for (const layer of line.eventLayers) {
      if (layer === null) continue;
      for (const key of LAYER_LIST_KEYS) lists.push(layer[key]);
    }
    if (line.extended !== undefined && line.extended !== null) {
      for (const key of EXTENDED_LIST_KEYS) lists.push(line.extended[key]);
    }
    for (const list of lists) {
      for (const e of list ?? []) {
        yield e.startTime;
        yield e.endTime;
      }
    }
    for (const n of line.notes ?? []) {
      yield n.startTime;
      yield n.endTime;
    }
  }
}

function timeTagsToIr(chart: RpeChart, tempo: TempoNorm, report: NormReport): IrTimeTag[] | undefined {
  if (chart.timeTags === undefined) return undefined;
  const out: IrTimeTag[] = [];
  chart.timeTags.forEach((tag, i) => {
    const b = Beats.tryFromTriple(tag.time);
    if (b === null) {
      report.add('normalized', { key: 'rpe.report.timeTagInvalid' }, `timeTags[${String(i)}].time`);
      return;
    }
    if (!fitsShifted(b.beat, tempo.shift)) {
      report.add('normalized', { key: 'rpe.report.timeTagBeatOutOfRange' }, `timeTags[${String(i)}].time`);
      return;
    }
    out.push({ name: tag.name, time: tempo.out(tag.time, b.beat) });
  });
  return out;
}

/**
 * Keys of Phira's Bezier map for this chart (F§6.7): every event with `bezier != 0` in the lists Phira scans
 * (alpha, moveX, moveY, rotate of every layer; paint, scaleX, scaleY, gif, incline, text, colour), whatever the
 * line kind (lists Phira does not draw are scanned too). Speed lists are never scanned.
 */
export function registeredBezierKeys(chart: RpeChart): ReadonlySet<string> {
  const keys = new Set<string>();
  const scan = (list: readonly RpeEvent<unknown>[] | null | undefined): void => {
    for (const e of list ?? []) {
      if (e.bezier !== undefined && e.bezier !== 0) keys.add(bezierKey(e.bezierPoints ?? [0, 0, 0, 0]));
    }
  };
  for (const line of chart.judgeLineList) {
    for (const layer of line.eventLayers) {
      if (layer === null) continue;
      scan(layer.alphaEvents);
      scan(layer.moveXEvents);
      scan(layer.moveYEvents);
      scan(layer.rotateEvents);
    }
    const ext = line.extended;
    if (ext === undefined || ext === null) continue;
    for (const key of EXTENDED_LIST_KEYS) scan(ext[key]);
  }
  return keys;
}

// ---------------------------------------------------------------------------------------------------------------
// Entry points
// ---------------------------------------------------------------------------------------------------------------

/** Normalisation (see the module header); the result carries the sidecar data (NormalizeResult.sidecar). */
export function normalizeRpeDetailed(decoded: RpeDecoded, ctx?: NormalizeContext): NormalizeResult {
  const report = new NormReport();
  reportDiagnostics(decoded.diagnostics, report);
  const chart = decoded.chart;
  const tempo = normalizeBpm(chart.BPMList, () => chartTriples(chart), report);
  let registry: ReadonlySet<string> | null = null;
  const env: NormEnv = {
    report,
    time: listTime(tempo),
    speedMode: ctx?.speedMode ?? 'legacy',
    textureKind: ctx === undefined ? defaultTextureKind : (p) => ctx.textureKind(p),
    sidecar: true,
    bezierRegistered: (points) => {
      registry ??= registeredBezierKeys(chart);
      return registry.has(bezierKey(points));
    },
  };
  const lines = chart.judgeLineList.map((l, i) => normalizeLine(l, i, env) as Mutable<IrLine>);
  fixFathers(lines, report);
  const bpmList: NonEmpty<IrBpm> = tempo.bpm;
  const out: Mutable<IrChart> = {
    META: metaToIr(decoded, report),
    BPMList: bpmList,
    judgeLineGroup: chart.judgeLineGroup ?? [],
    judgeLineList: lines,
    rootExtra: chart.extra,
  };
  const tags = timeTagsToIr(chart, tempo, report);
  if (tags !== undefined) out.timeTags = tags;
  if (chart.multiLineString !== undefined) out.multiLineString = chart.multiLineString;
  if (chart.multiScale !== undefined && Number.isFinite(chart.multiScale)) out.multiScale = chart.multiScale;
  if (chart.chartTime !== undefined && Number.isFinite(chart.chartTime)) out.chartTime = chart.chartTime;
  if (chart.xybind !== undefined) out.xybind = chart.xybind;
  return {
    chart: out,
    report: report.entries(),
    phiraWouldLoad: report.phiraWouldLoad,
    beatShift: tempo.shift === null ? null : Beats.toTriple(tempo.shift),
    sidecar: report.sidecar,
  };
}

export const normalizeRpe: NormalizeRpeFn = (decoded, ctx) => normalizeRpeDetailed(decoded, ctx);

/** The sidecar-only data of a normalisation result (LineRpeData.ignored entries with writeBack 'sidecar'). */
export function sidecarEntriesOf(result: NormalizeResult): readonly RpeSidecarEntry[] {
  return result.sidecar;
}
